import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApiClient } from '~/services/api'
import { Normalization } from '~/utils/normalization'
import { useLogger } from '~/composables/useLogger'
import { identityGeneration } from '~/utils/identityGeneration'

const logger = useLogger('DashboardStore')

type StoreId = string | number
interface DashboardStore {
  id?: StoreId
  name?: string
  [key: string]: unknown
}
interface DashboardError {
  message?: string
  statusCode?: number
  status?: number
  response?: { status?: number }
}
const isDashboardError = (value: unknown): value is DashboardError =>
  typeof value === 'object' &&
  value !== null &&
  ('message' in value || 'statusCode' in value || 'status' in value || 'response' in value)

export const useDashboardStore = defineStore('dashboard', () => {
  const storesApi = useApiClient().stores()

  // Current selected store — derived from cache + currentStoreId (keyed by storeId)
  const currentStoreId = ref<StoreId | null>(null)

  // Store data cache (keyed by store ID)
  const storeCache = ref<Record<string, DashboardStore>>({})

  const currentStore = computed<DashboardStore | null>(() => {
    if (currentStoreId.value === null) return null
    return storeCache.value[String(currentStoreId.value)] ?? null
  })

  // Loading and error states
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Computed properties
  const hasCurrentStore = computed(() => !!currentStore.value)
  const storeName = computed(
    () => Normalization.firstStringValue('Store', currentStore.value?.name) ?? 'Store'
  )

  /**
   * Fetch store details from API
   * @param {string|number} storeId - The store identifier
   * @param {boolean} force - Force refresh even if cached
   * @returns {Promise<Object>} Store data
   */
  const fetchStoreDetails = async (
    storeId: StoreId,
    force = false
  ): Promise<DashboardStore | null> => {
    const generation = identityGeneration.current()
    const cacheKey = String(storeId)
    // Return cached data if available and not forcing refresh
    const cachedStore = storeCache.value[cacheKey]
    if (!force && cachedStore !== undefined) {
      currentStoreId.value = storeId
      return cachedStore
    }

    loading.value = true
    error.value = null

    try {
      const data = await storesApi.show(String(storeId))
      // The previous account's store is not this account's data to hand back.
      if (!identityGeneration.isCurrent(generation)) return null

      // Cache the store data
      storeCache.value[cacheKey] = data
      currentStoreId.value = storeId
      return data
    } catch (err: unknown) {
      if (!identityGeneration.isCurrent(generation)) throw err
      const dashboardError: DashboardError = isDashboardError(err) ? err : {}
      error.value = Normalization.firstStringValue(
        'Failed to load store details',
        dashboardError.message
      )

      const statusCode =
        dashboardError.statusCode ?? dashboardError.status ?? dashboardError.response?.status
      if (statusCode === 403 || statusCode === 404) {
        currentStoreId.value = null
        const { [cacheKey]: _removed, ...remainingCache } = storeCache.value
        storeCache.value = remainingCache
      }

      logger.error('fetchStoreDetails', err)
      throw err
    } finally {
      if (identityGeneration.isCurrent(generation)) loading.value = false
    }
  }

  /**
   * Set the current store (useful when navigating from store list)
   * @param {string|number} storeId - The store identifier
   */
  const setCurrentStore = async (storeId: StoreId): Promise<void> => {
    if (storeId === currentStoreId.value && currentStore.value !== null) {
      // Already loaded, no need to fetch
      return
    }
    await fetchStoreDetails(storeId)
  }

  /**
   * Update store data in cache (useful after updates)
   * @param {string|number} storeId - The store identifier
   * @param {Object} updates - Partial store data to update
   */
  const updateStoreData = (storeId: StoreId, updates: Partial<DashboardStore>): void => {
    const cacheKey = String(storeId)
    const cachedStore = storeCache.value[cacheKey]
    if (cachedStore !== undefined) {
      storeCache.value[cacheKey] = {
        ...cachedStore,
        ...updates
      }
    }
  }

  /**
   * Clear store cache (useful on logout or when switching accounts)
   */
  const clearStoreCache = () => {
    storeCache.value = {}
    currentStoreId.value = null
    error.value = null
    // Owns the spinner too: a superseded fetch leaves its own `finally` write unmade.
    loading.value = false
  }

  /**
   * Get cached store data without fetching
   * @param {string|number} storeId - The store identifier
   * @returns {Object|null} Cached store data or null
   */
  const getCachedStore = (storeId: StoreId): DashboardStore | null => {
    return storeCache.value[String(storeId)] ?? null
  }

  const hasCachedStore = (storeId: StoreId): boolean => {
    return storeCache.value[String(storeId)] !== undefined
  }

  return {
    // State
    currentStore,
    currentStoreId,
    storeCache,
    loading,
    error,

    // Computed
    hasCurrentStore,
    storeName,

    // Actions
    fetchStoreDetails,
    setCurrentStore,
    updateStoreData,
    clearStoreCache,
    getCachedStore,
    hasCachedStore
  }
})
